import React from 'react';
import { Bot, User, Sparkles } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../../../utils/cn';
import { MessageAttachments, type DisplayAttachment } from '../molecules/MessageAttachments';

interface MessageBubbleProps {
  role: 'user' | 'assistant' | 'tool';
  content: string;
  attachments?: DisplayAttachment[];
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ role, content, attachments }) => {
  const isUser = role === 'user';
  const isTool = role === 'tool';

  return (
    <div
      className={cn(
        'flex gap-3 animate-in fade-in slide-in-from-bottom-2 duration-300',
        isUser ? 'flex-row-reverse' : 'flex-row'
      )}
      data-test-id={`message-bubble-${role}`}
    >
      {/* Avatar */}
      <div
        className={cn(
          'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
          role === 'assistant' && 'bg-indigo-600 shadow-lg shadow-indigo-600/20',
          role === 'tool' && 'bg-purple-600',
          role === 'user' && 'bg-gray-700'
        )}
      >
        {role === 'assistant' && <Bot className="h-5 w-5 text-white" />}
        {role === 'tool' && <Sparkles className="h-4 w-4 text-white" />}
        {role === 'user' && <User className="h-5 w-5 text-white" />}
      </div>

      {/* Bubble */}
      <div
        className={cn(
          'rounded-2xl px-5 py-3.5 max-w-[85%] text-sm md:text-base leading-relaxed',
          role === 'assistant' && 'bg-gray-800/40 border border-white/5 backdrop-blur-sm text-gray-100',
          isTool && 'bg-purple-900/30 text-purple-200 border border-purple-500/30 font-mono text-sm',
          isUser && 'bg-indigo-600 text-white'
        )}
      >
        {attachments && attachments.length > 0 && <MessageAttachments attachments={attachments} />}
        {content && <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>}
      </div>
    </div>
  );
};
